import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
    MapPin, 
    Globe, 
    Building2, 
    ArrowRight,
    Bookmark,
    ShieldCheck
} from 'lucide-react';

const ResourceCard = ({ resource, isAuthenticated, onSaveToBinder }) => {
    const {
        resource_id,
        name,
        description,
        organization_types = [],
        state,
        city,
        website,
        is_featured,
        can_edit = [],
    } = resource;

    const isClaimed = can_edit.length > 0;

    // Get primary type for card styling
    const primaryType = organization_types[0] || 'Resource';

    // Type color mapping - rainbow-inspired
    const typeColors = {
        'Employer': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
        'Training Provider': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
        'Service Provider': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        'Support Organization': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        'Government': 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
        'Foundation or Private Funder': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        'Higher Education': 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
        'Parents & Caregivers': 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
        'Workforce Alliance': 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
        'High School': 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
        'Transportation': 'bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200',
    };

    return (
        <Card 
            className={`group hover:border-primary/50 hover:shadow-md transition-all duration-200 ${
                is_featured ? 'ring-2 ring-primary/20' : ''
            }`}
            data-testid={`resource-card-${resource_id}`}
        >
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                            <h3 className="font-heading font-semibold text-base truncate">
                                {name}
                            </h3>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">
                                {city ? `${city}, ${state}` : state || 'Unknown'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        {isClaimed && (
                            <Badge variant="secondary" className="text-xs bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200 gap-1" data-testid={`haape-badge-${resource_id}`}>
                                <ShieldCheck className="h-3 w-3" />
                                HAAPE Member
                            </Badge>
                        )}
                        {is_featured && (
                            <Badge variant="default" className="text-xs">
                                Featured
                            </Badge>
                        )}
                        {isAuthenticated && onSaveToBinder && (
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-muted-foreground hover:text-primary"
                                onClick={(e) => { e.stopPropagation(); onSaveToBinder(resource); }}
                                title="Save to Binder"
                                data-testid={`save-binder-${resource_id}`}
                            >
                                <Bookmark className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {description}
                </p>

                {/* Organization Types */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                    {organization_types.slice(0, 3).map((type, index) => (
                        <Badge 
                            key={index}
                            variant="secondary"
                            className={`text-xs ${typeColors[type] || 'bg-muted text-muted-foreground'}`}
                        >
                            {type}
                        </Badge>
                    ))}
                    {organization_types.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                            +{organization_types.length - 3}
                        </Badge>
                    )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200"
                        data-testid={`view-resource-${resource_id}`}
                        onClick={() => window.location.href = `/resource/${resource_id}`}
                    >
                        View Details
                        <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                    {website && (
                        <a 
                            href={website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button variant="ghost" size="icon" className="shrink-0">
                                <Globe className="h-4 w-4" />
                            </Button>
                        </a>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default ResourceCard;
